import { monthApiToDisplay } from '@/components/shared/PersianMonthPickerFa';
import { accountingApi, type PayrollCalculatorProjectOption } from '@/lib/api/accounting';
import { fetchProjectManagerProjects, pmProjectLabel } from '@/lib/api/projectManagerBridge';

export function normalizeMonthApi(value: string): string {
  const v = value.replace(/\//g, '-').trim();
  const m = v.match(/^(\d{4})-(\d{1,2})$/);
  if (!m) return v;
  return `${m[1]}-${String(Number(m[2])).padStart(2, '0')}`;
}

function sortProjects(items: PayrollCalculatorProjectOption[]): PayrollCalculatorProjectOption[] {
  return [...items].sort((a, b) => {
    const scoreA = (a.has_attendance ? 1_000_000 : 0) + a.attendance_count;
    const scoreB = (b.has_attendance ? 1_000_000 : 0) + b.attendance_count;
    if (scoreB !== scoreA) return scoreB - scoreA;
    return a.label.localeCompare(b.label, 'fa');
  });
}

function mergeProjectMaps(
  ...maps: Array<Map<number, PayrollCalculatorProjectOption>>
): PayrollCalculatorProjectOption[] {
  const out = new Map<number, PayrollCalculatorProjectOption>();
  for (const map of maps) {
    for (const [id, row] of map) {
      const prev = out.get(id);
      if (!prev) {
        out.set(id, row);
        continue;
      }
      out.set(id, {
        ...prev,
        label: prev.label || row.label,
        has_attendance: prev.has_attendance || row.has_attendance,
        attendance_count: Math.max(prev.attendance_count, row.attendance_count),
        personnel_count: Math.max(prev.personnel_count, row.personnel_count),
      });
    }
  }
  return sortProjects([...out.values()]);
}

export async function loadPayrollProjectOptions(month: string): Promise<{
  month: string;
  month_label: string;
  projects: PayrollCalculatorProjectOption[];
  projects_with_attendance: number;
  warning?: string;
  sources: string[];
}> {
  const normalizedMonth = normalizeMonthApi(month);
  const sources: string[] = [];
  const maps: Array<Map<number, PayrollCalculatorProjectOption>> = [];
  let monthLabel = monthApiToDisplay(normalizedMonth);
  let warning: string | undefined;

  try {
    const data = await accountingApi.payrollCalculatorProjects(normalizedMonth);
    monthLabel = data.month_label || monthLabel;
    if (data.projects?.length) {
      sources.push('accounting-payroll');
      maps.push(new Map(data.projects.map((p) => [p.id, p])));
    }
  } catch (e) {
    warning = e instanceof Error ? e.message : 'بارگذاری لیست پروژه از API حقوق ناموفق بود.';
  }

  try {
    const lookups = await accountingApi.lookups();
    const fromLookups = (lookups.projects || []).map((p) => ({
      id: p.id,
      label: p.label,
      status: p.status,
      has_attendance: false,
      attendance_count: 0,
      personnel_count: 0,
    }));
    if (fromLookups.length) {
      sources.push('accounting-lookups');
      maps.push(new Map(fromLookups.map((p) => [p.id, p])));
    }
  } catch {
    /* ignore */
  }

  const pmRows = await fetchProjectManagerProjects();
  if (pmRows.length) {
    sources.push('project-manager');
    maps.push(
      new Map(
        pmRows.map((p) => [
          p.id,
          {
            id: p.id,
            label: pmProjectLabel(p),
            status: p.status || '',
            has_attendance: false,
            attendance_count: 0,
            personnel_count: 0,
          },
        ]),
      ),
    );
  }

  const projects = mergeProjectMaps(...maps);

  if (!projects.length) {
    warning =
      warning ??
      'هیچ پروژه‌ای یافت نشد. از پنل مدیریت پروژه، پروژه و حضور و غیاب را ثبت کنید.';
  } else if (!projects.some((p) => p.has_attendance)) {
    const extra =
      ' در این ماه برای هیچ پروژه‌ای رکورد حضور و غیاب ثبت نشده — می‌توانید پروژه را انتخاب کنید ولی جدول حقوق احتمالاً خالی می‌ماند.';
    warning = warning ? `${warning}${extra}` : extra.trim();
  }

  return {
    month: normalizedMonth,
    month_label: monthLabel,
    projects,
    projects_with_attendance: projects.filter((p) => p.has_attendance).length,
    warning,
    sources,
  };
}
